import { TimePeriod } from "../calendar/Calendar";
import { UserIdProp} from "./AuthenticatedUser";
import {UserGroupPrivilege} from "../user-groups/UserGroup";
import {Person} from "../common/Person";

export const USER_SELECT_API_ROUTE = "employees/select";

/**
 * Represents the work hours data associated with a user for a specific time period.
 *
 * The purpose of this interface is to track and analyze a user's scheduled and actual work hours
 * within a defined time window, ensuring adherence to mandatory requirements and identifying deviations.
 *
 * This can be used in contexts such as payroll systems, employee management applications, or productivity trackers.
 *
 * @property period - The time period during which the work hours are evaluated. Typically, this could represent a week, month, or custom duration as defined by the business logic.
 * @property mandatoryWorkhours - The required number of hours a user is expected to work within the defined period. This value is typically set based on company policies or employee contracts.
 * @property realWorkhours - The actual number of hours a user has worked during the defined period. This value is usually sourced from work logs or timesheets and may be used to calculate over/under time.
 */
export interface UserWorkhours {
    period: TimePeriod
    mandatoryWorkhours: number
    realWorkhours: number
}

/**
 * Represents a system user with personal and account-related details.
 *
 * This interface is used to capture and manage information associated with a user in the system,
 * including their email and account status. It extends base properties provided by UserIdProp
 * and Person to include uniquely identifying and personal information.
 *
 * @extends UserIdProp Combines unique identifier properties for the user account.
 * @extends Person Includes personal information such as name, age, or other related details.
 */
export interface User extends UserIdProp, Person
{
    email: string
    isDeleted: boolean
}

export type UserPrivileges = { [ key: string ]: UserGroupPrivilege }

/**
 * create the full name from a User object and return it
 * @param user
 */
export function fullName( person: Person ): string {
    return person.lastName + ', ' + person.firstName
}